import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAppConfigRedirect } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  DataCard,
  getAge,
  getCountryOfOrigin,
  getLanguages,
  getName,
  useReportRedirect,
} from '@/reports/index';
import { serializedTypes } from '@/reports/types';
import { DataList } from '@/theme/index';
import { nameize, personSearchByBvId } from '@/utils/index';

interface PotentialOwnerCardProps {
  owner: serializedTypes.SerializedPerson;
  thumbRating: () => JSX.Element;
}

export const PotentialOwnerCard: React.FC<PotentialOwnerCardProps> = ({
  owner,
  thumbRating,
}) => {
  const name = nameize(getName(owner));
  const age = getAge(owner);
  const languages = getLanguages(owner);
  const countryOfOrigin = getCountryOfOrigin(owner);
  const bvId = owner.identity.ids[0] || '';

  const isInPartialAddressesVariation = useFeatureIsOn('oar-1075');

  const personSearch = {
    fName: owner.identity.names[0].parsed?.first || '',
    mName: owner.identity.names[0].parsed?.middle || '',
    lName: owner.identity.names[0].parsed?.last || '',
    age: age || '',
    city: owner.contact.addresses?.[0]?.parsed?.city || '',
    state: owner.contact.addresses?.[0]?.parsed?.state || 'All',
    showPartialAddresses: isInPartialAddressesVariation,
  };
  const {
    links: { baseUrl },
    featureFlags: {
      shouldOpenReportInCurrentTab,
      hasContactSearchAsDefaultSearch,
    },
  } = useAppConstantsContext();
  const { redirectToSearchPersonUrl } = useReportRedirect();
  const {
    routingUtils: { searchContactUrl },
  } = useAppConfigRedirect();
  const handleClick = () => {
    if (hasContactSearchAsDefaultSearch) {
      window.open(
        `${baseUrl}${searchContactUrl({ name })}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    personSearchByBvId(
      bvId,
      personSearch,
      redirectToSearchPersonUrl,
      baseUrl,
      shouldOpenReportInCurrentTab
    );
  };

  const buttonLabel = () => {
    if (hasContactSearchAsDefaultSearch) {
      return 'Search Contact';
    }

    if (bvId) {
      return 'View Person Report';
    }

    return 'Search Person';
  };

  return (
    <DataCard
      titleProps={{ title: name, onClick: handleClick }}
      buttonProps={{
        label: buttonLabel(),
        onClick: handleClick,
      }}
      thumbRating={thumbRating}
    >
      <DataList
        rowSpacing={1}
        colSpacing={1.25}
        items={[
          ['Age', age || ''],
          ['Languages Spoken', languages],
          ['Country of origin', countryOfOrigin],
        ]}
      />
    </DataCard>
  );
};
